import Head from 'next/head';
import Link from 'next/link';

function formatDateFr(dateStr) {
  const [y, m, d] = dateStr.split('-');
  const months = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
  return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
}

export default function MystereDailyPage({ date, dateFormatted }) {
  const url = `https://fenkparet.com/jeux/mystere/${date}`;
  const today = new Date().toISOString().slice(0, 10);
  const isToday = date === today;
  const isPast = date < today;

  return (
    <>
      <Head>
        <title>Mystère du {dateFormatted}: Wordle Français | Fenkparet</title>
        <meta name="description" content={`Mystère du ${dateFormatted} — devinez le mot français du jour en 6 tentatives. Jouez chaque jour à ce Wordle en français sur Fenkparet !`} />
        <meta name="keywords" content={`mystère ${date}, wordle français ${dateFormatted}, mot du jour, jeu de mots, fenkparet`} />
        <link rel="canonical" href={url} />

        <meta property="og:title" content={`Mystère du ${dateFormatted} — Wordle Français`} />
        <meta property="og:description" content="Devinez le mot français du jour en 6 tentatives !" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:locale" content="fr_FR" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`Mystère du ${dateFormatted}`} />
        <meta name="twitter:description" content="Devinez le mot français du jour en 6 tentatives !" />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `Mystère du ${dateFormatted} — Wordle Français`,
          description: `Défi Mystère du ${dateFormatted} — devinez le mot du jour en français en 6 tentatives.`,
          datePublished: `${date}T00:00:00Z`,
          url,
          publisher: { '@type': 'Organization', name: 'Fenkparet', url: 'https://fenkparet.com' },
          inLanguage: 'fr',
        }) }} />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://fenkparet.com' },
            { '@type': 'ListItem', position: 2, name: 'Jeux', item: 'https://fenkparet.com/jeux' },
            { '@type': 'ListItem', position: 3, name: 'Mystère', item: 'https://fenkparet.com/jeux/mystere' },
            { '@type': 'ListItem', position: 4, name: dateFormatted, item: url },
          ],
        }) }} />
      </Head>

      <div className="min-h-screen bg-theme-primary">
        <div className="max-w-2xl mx-auto px-4 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-theme-secondary mb-8">
            <Link href="/" className="hover:text-accent">Accueil</Link>
            <span>›</span>
            <Link href="/jeux" className="hover:text-accent">Jeux</Link>
            <span>›</span>
            <Link href="/jeux/mystere" className="hover:text-accent">Mystère</Link>
            <span>›</span>
            <span className="text-theme-primary">{dateFormatted}</span>
          </nav>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-theme-primary mb-2">
              Mystère — Mot du jour
            </h1>
            <p className="text-theme-secondary">{dateFormatted}</p>
          </div>

          {/* Main card */}
          <div className="bg-theme-secondary border border-theme rounded-2xl p-8 text-center mb-8 shadow-sm"
            style={{ borderTop: '4px solid #2563eb' }}>
            <p className="text-sm text-theme-secondary mb-4 font-medium uppercase tracking-wide">Mystère</p>

            {/* Letter boxes preview */}
            <div className="flex justify-center gap-2 mb-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="w-12 h-12 border-2 border-gray-300 rounded flex items-center justify-center text-xl font-bold text-gray-300">
                  ?
                </div>
              ))}
            </div>

            <h2 className="text-lg font-semibold text-theme-primary mb-1">Devinez le mot du jour</h2>
            <p className="text-sm text-theme-secondary">Un mot de 5 lettres en français à trouver en 6 tentatives.</p>
          </div>

          {/* Info grid */}
          <div className="grid grid-cols-3 gap-4 mb-8 text-center">
            <div className="bg-theme-secondary rounded-xl p-4 border border-theme">
              <div className="text-2xl mb-1">5️⃣</div>
              <div className="text-xs text-theme-secondary">Lettres</div>
            </div>
            <div className="bg-theme-secondary rounded-xl p-4 border border-theme">
              <div className="text-2xl mb-1">6️⃣</div>
              <div className="text-xs text-theme-secondary">Tentatives</div>
            </div>
            <div className="bg-theme-secondary rounded-xl p-4 border border-theme">
              <div className="text-2xl mb-1">📅</div>
              <div className="text-xs text-theme-secondary">Quotidien</div>
            </div>
          </div>

          {/* Color legend */}
          <div className="bg-theme-secondary rounded-xl p-4 mb-8 border border-theme">
            <p className="text-sm font-medium text-theme-primary mb-3">Comment jouer :</p>
            <div className="space-y-2 text-sm text-theme-secondary">
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold" style={{ background: '#6aaa64' }}>A</span>
                <span>Bonne lettre, bonne place</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold" style={{ background: '#c9b458' }}>A</span>
                <span>Bonne lettre, mauvaise place</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold" style={{ background: '#787c7e' }}>A</span>
                <span>Lettre absente du mot</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            {isToday ? (
              <Link href="/jeux/mystere"
                className="inline-block text-white px-8 py-4 rounded-xl font-bold text-lg transition-colors"
                style={{ background: '#2563eb' }}>
                Commencer la partie
              </Link>
            ) : isPast ? (
              <div className="space-y-3">
                <p className="text-theme-secondary text-sm">Ce défi est terminé.</p>
                <Link href="/jeux/mystere"
                  className="inline-block text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                  style={{ background: '#2563eb' }}>
                  Jouer le défi d&apos;aujourd&apos;hui
                </Link>
              </div>
            ) : (
              <p className="text-theme-secondary">Ce défi sera disponible le {dateFormatted}.</p>
            )}
          </div>

          {/* Internal links */}
          <div className="mt-12 pt-8 border-t border-theme">
            <p className="text-sm text-theme-secondary text-center mb-4">Vous aimez Mystère ? Découvrez aussi</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/jeux/brin"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-theme hover:bg-theme-secondary transition-colors text-sm font-medium">
                🟢 Brin — Jeu de lettres quotidien
              </Link>
              <Link href="/jeux/sudoku"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-theme hover:bg-theme-secondary transition-colors text-sm font-medium">
                🟣 Sudoku — Défi logique
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export async function getStaticPaths() {
  const paths = [];
  const startDate = new Date('2026-01-01');
  const endDate = new Date('2026-12-31');
  const current = new Date(startDate);
  while (current <= endDate) {
    paths.push({ params: { date: current.toISOString().slice(0, 10) } });
    current.setDate(current.getDate() + 1);
  }
  return { paths, fallback: false };
}

export async function getStaticProps({ params }) {
  const { date } = params;
  return { props: { date, dateFormatted: formatDateFr(date) } };
}
